/**
 * FO-035 (batch-16) — HR-gespreksverslag: types, DTO's en Zod-schemas.
 *
 * Een gestructureerd verslag (per gesprekstype) op STREEP-briefpapier, 1:1 met
 * een `HrGesprek`. Intern (CONCEPT) tot "Vrijgeven voor medewerker"
 * (VRIJGEGEVEN). De medewerker kan in de portal tekenen voor akkoord (hergebruik
 * `DocumentSignature` op het gekoppelde Document) en/of reageren/bezwaar maken.
 *
 * Gedeeld schema = de API én het form valideren ermee (inline veldfouten).
 */

import { z } from 'zod'
import { idParam } from './id-validators'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij generatie.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'
// batch-47 gespreksverslagen (spec 90) — het event-gedreven statusverloop hangt
// als `verloop` onder de bestaande DTO's.
import {
  GESPREKSVERSLAG_ACTIEPUNT_PRIORITEIT,
  type GespreksverslagActiepuntPrioriteit,
  type GespreksverslagTekenverklaring,
  type GespreksverslagVerloopDTO,
} from './gespreksverslag-status'

// ─── Enums (matching Prisma) ──────────────────────────────────────────────────

// FO-378 (batch-44) — HERZIENING: "Nieuwe versie maken" ná vrijgave; het
// verslag is terug in bewerking terwijl de laatst vrijgegeven versie (portal)
// intact blijft. Opnieuw vrijgeven -> VRIJGEGEVEN + versie N+1.
export const GESPREKSVERSLAG_STATUS = ['CONCEPT', 'VRIJGEGEVEN', 'HERZIENING'] as const
export type GespreksverslagStatus = (typeof GESPREKSVERSLAG_STATUS)[number]

export const GESPREKSVERSLAG_STATUS_LABELS: Record<GespreksverslagStatus, string> = {
  CONCEPT:     'Concept (intern)',
  VRIJGEGEVEN: 'Vrijgegeven',
  HERZIENING:  'Herziening (in bewerking)',
}

export const GESPREKSVERSLAG_REACTIE_SOORT = ['REACTIE', 'BEZWAAR', 'GEZIEN_NIET_AKKOORD'] as const
export type GespreksverslagReactieSoort = (typeof GESPREKSVERSLAG_REACTIE_SOORT)[number]

export const GESPREKSVERSLAG_REACTIE_SOORT_LABELS: Record<GespreksverslagReactieSoort, string> = {
  REACTIE:             'Reactie',
  BEZWAAR:             'Bezwaar',
  GEZIEN_NIET_AKKOORD: 'Gezien, niet akkoord',
}

// FO-309 P3 — actiehouder van een afspraak: STREEP (opsteller) of de medewerker.
export const GESPREKSVERSLAG_AFSPRAAK_ACTIEHOUDER = ['STREEP', 'MEDEWERKER'] as const
export type GespreksverslagAfspraakActiehouder =
  (typeof GESPREKSVERSLAG_AFSPRAAK_ACTIEHOUDER)[number]

export const GESPREKSVERSLAG_AFSPRAAK_ACTIEHOUDER_LABELS: Record<
  GespreksverslagAfspraakActiehouder,
  string
> = {
  STREEP:     'STREEP',
  MEDEWERKER: 'Medewerker',
}

// ─── Verslag-format (gestructureerde inhoud) ──────────────────────────────────

/**
 * De inhoud-secties van het verslag. Eén gedeelde shape; het concrete "format
 * per type" zit in de UI-labels/hints, niet in een afwijkend schema. `besproken`
 * is de kern en verplicht; de rest is optioneel zodat een kort startgesprek niet
 * alle velden hoeft te vullen.
 */
export const gespreksverslagInhoudSchema = z.object({
  aanwezigen: z.string().trim().max(2000).optional().default(''),
  aanleiding: z.string().trim().max(4000).optional().default(''),
  besproken: z.string().trim().min(1, 'Beschrijf wat er besproken is').max(20000),
  afspraken: z.string().trim().max(20000).optional().default(''),
  vervolg: z.string().trim().max(8000).optional().default(''),
  extra: z.string().trim().max(8000).optional().default(''),
})
export type GespreksverslagInhoud = z.infer<typeof gespreksverslagInhoudSchema>

/** De velddefinities per sectie (labels + hints) voor het invoerformulier. */
export interface GespreksverslagVeldDef {
  key: keyof GespreksverslagInhoud
  label: string
  hint: string
  verplicht: boolean
}

// FO-309 P3 — het formulier is Inleiding + Inhoud gesprek + (gestructureerde)
// actielijst. `aanleiding` = Inleiding en `besproken` = Inhoud gesprek (mappen op
// de bestaande JSON-keys zodat de PDF-render en oude verslagen ongemoeid blijven;
// alleen de labels wijzigen). De vrije-tekst-`afspraken` is uit het formulier
// gehaald (nu `GespreksverslagAfspraak`-regels), maar blijft leesbaar op oude
// verslagen. `aanwezigen`/`vervolg`/`extra` blijven compacte optionele velden.
export const GESPREKSVERSLAG_VELDEN: GespreksverslagVeldDef[] = [
  { key: 'aanleiding', label: 'Inleiding', hint: 'Korte inleiding: aanleiding en context van het gesprek.', verplicht: true },
  { key: 'besproken', label: 'Inhoud gesprek', hint: 'Wat is er besproken?', verplicht: true },
  { key: 'aanwezigen', label: 'Aanwezigen', hint: 'Wie waren bij het gesprek aanwezig?', verplicht: false },
  { key: 'vervolg', label: 'Vervolg', hint: 'Welke vervolgstappen of vervolgafspraak?', verplicht: false },
  { key: 'extra', label: 'Aanvullend', hint: 'Aanvullende opmerkingen (bijv. gevolg bij herhaling).', verplicht: false },
]

/** Min. tekens voor Inleiding + Inhoud gesprek bij definitief maken (FO §5). */
export const GESPREKSVERSLAG_MIN_TEKENS = 20

// ─── Zod: mutaties ────────────────────────────────────────────────────────────

// FO-309 P3 — één afspraak-regel (actielijst). Verplichte deadline (yyyy-MM-dd);
// actiehouder STREEP (opsteller) of MEDEWERKER (de verslag-medewerker). `id` leeg
// = nieuwe regel, gevuld = bestaande regel bijwerken.
export const gespreksverslagAfspraakSchema = z.object({
  id: z.string().optional(),
  omschrijving: z.string().trim().min(1, 'Omschrijf de afspraak').max(2000),
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een deadline'),
  actiehouderType: z.enum(GESPREKSVERSLAG_AFSPRAAK_ACTIEHOUDER).default('MEDEWERKER'),
})
export type GespreksverslagAfspraakInput = z.infer<typeof gespreksverslagAfspraakSchema>

/**
 * Verslag aanmaken/bijwerken (concept) -> genereert het briefpapier-document.
 * De actielijst wordt hier meegestuurd (diff-upsert server-side). Het concept-
 * pad is bewust lenient (leeg toegestaan); de strikte eisen (min. tekens, >= 1
 * afspraak met deadline in de toekomst) gelden pas bij definitief maken zodat een
 * tussentijds concept opslaan nooit blokkeert en oude verslagen leesbaar blijven.
 */
export const upsertGespreksverslagSchema = z.object({
  inhoud: gespreksverslagInhoudSchema,
  afspraken: z.array(gespreksverslagAfspraakSchema).max(50).default([]),
})
export type UpsertGespreksverslagInput = z.infer<typeof upsertGespreksverslagSchema>

/**
 * Gedeelde definitief-maak-validatie (FO §5): Inleiding + Inhoud >= 20 tekens en
 * minstens één afspraak met een deadline in de toekomst. Pure functie zodat de
 * API én het form dezelfde regels toepassen (inline veldfouten). `vandaagYmd` =
 * NL-kalenderdag (yyyy-MM-dd) zodat de "in de toekomst"-check tz-vast is.
 */
export interface DefinitiefValidatieInput {
  inhoud: Pick<GespreksverslagInhoud, 'aanleiding' | 'besproken'>
  afspraken: Array<{ deadline: string }>
}
export function valideerDefinitief(
  input: DefinitiefValidatieInput,
  vandaagYmd: string,
): { veld: string; message: string }[] {
  const fouten: { veld: string; message: string }[] = []
  if ((input.inhoud.aanleiding ?? '').trim().length < GESPREKSVERSLAG_MIN_TEKENS) {
    fouten.push({ veld: 'aanleiding', message: `Inleiding is verplicht (min. ${GESPREKSVERSLAG_MIN_TEKENS} tekens)` })
  }
  if ((input.inhoud.besproken ?? '').trim().length < GESPREKSVERSLAG_MIN_TEKENS) {
    fouten.push({ veld: 'besproken', message: `Inhoud gesprek is verplicht (min. ${GESPREKSVERSLAG_MIN_TEKENS} tekens)` })
  }
  const heeftToekomst = input.afspraken.some((a) => a.deadline > vandaagYmd)
  if (input.afspraken.length === 0) {
    fouten.push({ veld: 'afspraken', message: 'Voeg minstens één afspraak met deadline toe' })
  } else if (!heeftToekomst) {
    fouten.push({ veld: 'afspraken', message: 'Minstens één afspraak moet een deadline in de toekomst hebben' })
  }
  return fouten
}

/**
 * FO-378 — "Nieuwe versie maken" op een vrijgegeven verslag. De versiereden is
 * verplicht (VIA_FLOW-herziening): de medewerker en de tijdlijn moeten kunnen
 * zien wáárom er een nieuwe versie kwam. Gedeeld schema: API + form.
 */
export const nieuweVerslagVersieSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden voor de nieuwe versie (min. 5 tekens)')
    .max(1000),
  /**
   * batch-68 handtekening (FO-345 §3.4, D4) - er zijn al handtekeningen: de
   * opsteller bevestigt dat die vervallen (409 HAS_SIGNATURES zonder deze vlag).
   */
  bevestigVervallen: z.boolean().optional(),
})
export type NieuweVerslagVersieInput = z.infer<typeof nieuweVerslagVersieSchema>

/** Portal: reactie/bezwaar van de medewerker op een vrijgegeven verslag. */
export const gespreksverslagReactieSchema = z
  .object({
    soort: z.enum(GESPREKSVERSLAG_REACTIE_SOORT),
    tekst: z.string().trim().max(4000).optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.soort === 'REACTIE' || v.soort === 'BEZWAAR') && (!v.tekst || v.tekst.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tekst'],
        message: 'Geef een toelichting bij je reactie of bezwaar',
      })
    }
  })
export type GespreksverslagReactieInput = z.infer<typeof gespreksverslagReactieSchema>

// ─── DTO's ────────────────────────────────────────────────────────────────────

export interface GespreksverslagReactieDTO {
  id: string
  soort: GespreksverslagReactieSoort
  tekst: string | null
  employeeId: string
  /** Naam van de reagerende medewerker (alleen backoffice-DTO, niet portal-extern). */
  employeeNaam: string | null
  createdAt: string
}

export interface GespreksverslagSignatureDTO {
  id: string
  /** batch-68 (FO-345): null bij de gespreksvoerder die met zijn gebruikersaccount tekent. */
  employeeId: string | null
  employeeNaam: string | null
  signedAt: string
  /** FO-378 — bij welke verslagversie de handtekening hoort (null = legacy). */
  versionNr: number | null
  /** batch-68 (FO-345) — het vak (rol) waarin getekend is. */
  rol?: 'MEDEWERKER' | 'WERKGEVER' | 'GESPREKSVOERDER' | 'LEIDINGGEVENDE' | 'HR' | 'GETUIGE' | 'OVERIG'
  /** batch-68 (FO-345) — vervallen door een nieuwe versie (historie). */
  vervallenOp?: string | null
}

/**
 * FO-378 — één vastgelegde (vrijgegeven) verslagversie in de versie-tijdlijn:
 * wie/wanneer/reden + de akkoord-status van precies die versie.
 */
export interface GespreksverslagVersieDTO {
  versionNr: number
  /** Versiereden (verplicht bij een herziening; null voor v1). */
  reden: string | null
  definitiefOp: string | null
  ondertekendStreepOp: string | null
  vrijgegevenOp: string
  vrijgegevenDoorNaam: string | null
  /** Handtekeningen op DEZE versie. */
  handtekeningen: GespreksverslagSignatureDTO[]
  /** Stilzwijgend akkoord op DEZE versie (nooit een handtekening). */
  stilzwijgendAkkoordOp: string | null
  /** Aantal reacties/bezwaren op deze versie. */
  aantalReacties: number
}

/** FO-309 P3 — één afspraak-regel op het verslag (afvinkbaar). */
export interface GespreksverslagAfspraakDTO {
  id: string
  omschrijving: string
  /** yyyy-MM-dd (NL-kalenderdag) — de richtdatum voor de herinnering. */
  deadline: string
  actiehouderType: GespreksverslagAfspraakActiehouder
  /** Naam van de actiehouder (medewerker of opsteller) voor weergave. */
  actiehouderNaam: string | null
  /** ISO-tijd waarop de afspraak is afgevinkt, of null. */
  afgerondOp: string | null
  volgorde: number
  // ── batch-39 gesprekken (FO-332) — actiepunt-tabel (v2) ──
  /** Kolom "Afspraken" (null bij v1-regels). */
  afspraakOmschrijving: string | null
  deadlineVorm: GespreksverslagDeadlineVorm
  /** Bewoording zoals het document print; null → toon de deadline als datum. */
  deadlineBewoording: string | null
  status: GespreksverslagActiepuntStatus
  /** Actiehouders (v2); leeg bij v1-regels (gebruik dan actiehouderNaam). */
  houders: GespreksverslagHouderDTO[]
  // ── batch-47 (spec 90 §11) - zichtbaar losgekoppeld van de documentstatus ──
  prioriteit: GespreksverslagActiepuntPrioriteit
  gedeeldMetMedewerker: boolean
  bewijsstukDocumentId: string | null
  afrondingsOpmerking: string | null
}

/**
 * FO-309 P3 — status-tijdlijn afgeleid uit de keten-tijdstempels. Elke stap is
 * `null` (nog niet) of een ISO-tijd. Geen aparte statuskolom nodig.
 */
export interface GespreksverslagTijdlijnDTO {
  conceptOp: string
  definitiefOp: string | null
  ondertekendStreepOp: string | null
  verzondenOp: string | null
  getekendMedewerkerOp: string | null
  laatsteReactieOp: string | null
  /** batch-39 gesprekken (FO-332) — stilzwijgend akkoord geregistreerd op. */
  stilzwijgendAkkoordOp: string | null
}

/** Backoffice-DTO: HR/teamleider ziet het volledige verslag + status + reacties. */
export interface GespreksverslagDTO {
  id: string
  gesprekId: string
  employeeId: string
  employeeNaam: string | null
  status: GespreksverslagStatus
  inhoud: GespreksverslagInhoud
  documentId: string | null
  exemplaarNummer: string | null
  registratieId: string | null
  opgesteldDoor: string
  opgesteldDoorNaam: string | null
  // FO-309 P3 — keten-tijdstempels (definitief -> STREEP tekent -> verzenden).
  definitiefDoor: string | null
  definitiefDoorNaam: string | null
  definitiefOp: string | null
  ondertekendStreepDoor: string | null
  ondertekendStreepDoorNaam: string | null
  ondertekendStreepOp: string | null
  vrijgegevenDoor: string | null
  vrijgegevenDoorNaam: string | null
  /** = verzonden naar de medewerker (portal + mail). */
  vrijgegevenOp: string | null
  createdAt: string
  updatedAt: string
  /** FO-309 P3 — gestructureerde actielijst. */
  afspraken: GespreksverslagAfspraakDTO[]
  // ── batch-39 gesprekken (FO-332) — §2-veldenschema (v2) ──
  /** 1 = oud vrije-velden-formaat (`inhoud`), 2 = het §2-veldenschema. */
  veldenVersie: number
  /** yyyy-MM-dd zoals in het verslag; null → val terug op gesprek.geplandOp. */
  gespreksDatum: string | null
  onderwerp: string | null
  aanwezig: string | null
  locatie: string | null
  inleiding: string | null
  verslag: string | null
  samenvatting: string | null
  stilzwijgendAkkoordOp: string | null
  /** "Volgende afspraak"-cyclusregels. */
  vervolgAfspraken: GespreksverslagVervolgDTO[]
  /** FO-309 P3 — status-tijdlijn (afgeleid uit de tijdstempels + tekenen/reacties). */
  tijdlijn: GespreksverslagTijdlijnDTO
  /** Handtekeningen voor akkoord (DocumentSignature op het gekoppelde Document). */
  handtekeningen: GespreksverslagSignatureDTO[]
  reacties: GespreksverslagReactieDTO[]
  // ── FO-378 (batch-44) — versiebeleid ──
  /** Vastgelegde (vrijgegeven) versies, oplopend; leeg zolang nooit vrijgegeven. */
  versies: GespreksverslagVersieDTO[]
  /** Open herziening (status HERZIENING): wie/wanneer/waarom. */
  herzieningGestartOp: string | null
  herzieningGestartDoorNaam: string | null
  herzieningReden: string | null
  /**
   * Verzuim-redactie: bij een VERZUIMGESPREK-verslag is de inhoud `null`/leeg
   * voor callers zonder VERZUIM.canView. Dan is dit true (toon redactie-melding).
   */
  geredigeerd: boolean
  /**
   * batch-47 (spec 90) — het event-gedreven statusverloop: statusbalk,
   * append-only tijdlijn, ondertekenaars, termijnstand en addenda. Null alleen
   * als het verslag tussentijds verdween.
   */
  verloop: GespreksverslagVerloopDTO | null
  /**
   * batch-68 handtekening (FO-345 §3.5) - de handtekening van de
   * gespreksvoerder: tekent hij (instelling), met welk bevestigingsniveau, en
   * staat zijn handtekening al op de werkversie of de vastgelegde versie.
   * Optioneel: oudere API-antwoorden kennen dit veld niet.
   */
  ondertekening?: GespreksverslagOndertekeningDTO
}

/** batch-68 handtekening (FO-345 §3.5) - zie `GespreksverslagDTO.ondertekening`. */
export interface GespreksverslagOndertekeningDTO {
  /** Instelling "gespreksvoerder tekent bij vrijgave" (default aan). */
  gespreksvoerderTekent: boolean
  /** STANDAARD of VERHOOGD (opnieuw inloggen vlak voor het tekenen). */
  bevestiging: 'STANDAARD' | 'VERHOOGD'
  gespreksvoerderUserId: string
  gespreksvoerderNaam: string | null
  /** ISO - actieve (niet vervallen) handtekening van de gespreksvoerder, anders null. */
  gespreksvoerderGetekendOp: string | null
}

// ═══ batch-39 gesprekken (FO-332) — §2-veldenschema (v2) ═══════════════════════
//
// Het aangeleverde gespreksverslag-template als formulier: kop-blok (datum/
// onderwerp/aanwezig/locatie), grote vakken (inleiding/verslag/samenvatting),
// een actiepunten-tabel (afspraak · actiepunt · wie · deadline-VORM · status)
// en "Volgende afspraak"-cyclusregels. veldenVersie 2; oude vrije-velden-
// verslagen (versie 1) blijven leesbaar via `inhoud`.

export const GESPREKSVERSLAG_DEADLINE_VORM = [
  'KORTE_TERMIJN',
  'EINDE_MAAND',
  'NA_MAAND',
  'DATUM',
] as const
export type GespreksverslagDeadlineVorm = (typeof GESPREKSVERSLAG_DEADLINE_VORM)[number]

export const GESPREKSVERSLAG_DEADLINE_VORM_LABELS: Record<GespreksverslagDeadlineVorm, string> = {
  KORTE_TERMIJN: 'Korte termijn (+2 weken)',
  EINDE_MAAND: 'Einde maand',
  NA_MAAND: 'Na maand',
  DATUM: 'Specifieke datum',
}

export const GESPREKSVERSLAG_ACTIEPUNT_STATUS = ['OPEN', 'AFGEHANDELD'] as const
export type GespreksverslagActiepuntStatus = (typeof GESPREKSVERSLAG_ACTIEPUNT_STATUS)[number]

export const GESPREKSVERSLAG_ACTIEPUNT_STATUS_LABELS: Record<
  GespreksverslagActiepuntStatus,
  string
> = {
  OPEN: 'Open',
  AFGEHANDELD: 'Afgehandeld',
}

/**
 * STREEP-default voor de akkoord-bloktekst in het verslag-document (seed uit
 * het aangeleverde template, taalfouten hersteld; `{dagen}` wordt bij het
 * renderen vervangen door GesprekTypeConfig.stilzwijgendAkkoordDagen).
 * Beheerbaar per gesprekstype via het gesprekstypen-beheer.
 */
export const DEFAULT_STILZWIJGEND_AKKOORD_TEKST =
  'Graag ontvangen wij een handtekening waarmee je akkoord gaat met de inhoud van dit verslag. ' +
  'Hierboven is de mogelijkheid om te reageren. Als er binnen {dagen} dagen geen reactie wordt ' +
  'gegeven, gaan wij ervan uit dit verslag correct te hebben verwoord en dat je akkoord gaat met de inhoud.'

/**
 * Resolve de akkoord-bloktekst voor het document: de beheerde tekst per
 * gesprekstype, of de STREEP-default; `{dagen}` wordt vervangen door het aantal
 * dagen tot stilzwijgend akkoord.
 */
export function resolveAkkoordTekst(tekst: string | null | undefined, dagen: number): string {
  const basis = tekst && tekst.trim().length > 0 ? tekst : DEFAULT_STILZWIJGEND_AKKOORD_TEKST
  return basis.replace(/\{dagen\}/g, String(dagen))
}

/** Eén actiehouder: een app-medewerker (picker) óf een vrije naam (extern). */
export const gespreksverslagHouderSchema = z
  .object({
    employeeId: z.string().trim().min(1).optional(),
    vrijeNaam: z.string().trim().min(1, 'Vul een naam in').max(200).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.employeeId && !v.vrijeNaam) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vrijeNaam'],
        message: 'Kies een medewerker of vul een naam in',
      })
    }
    if (v.employeeId && v.vrijeNaam) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vrijeNaam'],
        message: 'Kies óf een medewerker óf een vrije naam, niet beide',
      })
    }
  })
export type GespreksverslagHouderInput = z.infer<typeof gespreksverslagHouderSchema>

/**
 * Eén actiepunt-regel (v2). Een regel vereist actiepunt-tekst + wie +
 * deadline-vorm (FO-332 §7); nul regels is toegestaan (niet elk gesprek levert
 * acties). De vorm-parameters: DATUM → `deadlineDatum`, NA_MAAND → verplicht
 * `deadlineMaand`, EINDE_MAAND → optioneel `deadlineMaand` (leeg = huidige
 * maand), KORTE_TERMIJN → geen.
 */
export const gespreksverslagActiepuntSchema = z
  .object({
    id: z.string().optional(),
    /** Kolom "Afspraken" — de overkoepelende afspraak (optioneel). */
    afspraakOmschrijving: z.string().trim().max(2000).optional().default(''),
    /** Kolom "Actiepunten" — het actiepunt zelf (verplicht). */
    omschrijving: z.string().trim().min(1, 'Omschrijf het actiepunt').max(2000),
    houders: z.array(gespreksverslagHouderSchema).min(1, 'Kies wie dit oppakt').max(10),
    deadlineVorm: z.enum(GESPREKSVERSLAG_DEADLINE_VORM),
    /** yyyy-MM-dd; verplicht bij vorm DATUM. */
    deadlineDatum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een datum')
      .optional(),
    /** yyyy-MM; verplicht bij NA_MAAND, optioneel bij EINDE_MAAND. */
    deadlineMaand: z
      .string()
      .regex(/^\d{4}-\d{2}$/, 'Kies een maand')
      .optional(),
    status: z.enum(GESPREKSVERSLAG_ACTIEPUNT_STATUS).default('OPEN'),
    // ── batch-47 (spec 90 §11) - PO-velden per actie ──
    prioriteit: z.enum(GESPREKSVERSLAG_ACTIEPUNT_PRIORITEIT).default('NORMAAL'),
    /** false = intern (alleen STREEP ziet het), true = gedeeld met de medewerker. */
    gedeeldMetMedewerker: z.boolean().default(true),
    /** Document-id van het bewijsstuk bij afronding (optioneel). */
    bewijsstukDocumentId: z.string().trim().min(1).optional(),
    /** Toelichting bij het afronden (wat is er gedaan). */
    afrondingsOpmerking: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.deadlineVorm === 'DATUM' && !v.deadlineDatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deadlineDatum'],
        message: 'Kies een datum bij deze deadline-vorm',
      })
    }
    if (v.deadlineVorm === 'NA_MAAND' && !v.deadlineMaand) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deadlineMaand'],
        message: 'Kies een maand bij deze deadline-vorm',
      })
    }
  })
export type GespreksverslagActiepuntInput = z.infer<typeof gespreksverslagActiepuntSchema>

/**
 * Eén "Volgende afspraak"-regel: gesprekstype + datum (optioneel mét tijd) óf
 * "te plannen door".
 *
 * batch-47 (spec 90 §11a): `tijd` is een NL-wandklok `HH:mm` en optioneel - een
 * vervolgafspraak kan ook "in week X" zijn. Mét tijd wordt het een concreet
 * moment dat direct als agenda-gesprek ingepland kan worden.
 */
export const gespreksverslagVervolgSchema = z
  .object({
    id: z.string().optional(),
    gesprekTypeConfigId: z.string().trim().min(1).optional(),
    typeLabel: z.string().trim().min(1, 'Vul het gesprekstype in').max(200),
    /** yyyy-MM-dd; leeg = nog te plannen. */
    datum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een datum')
      .optional(),
    /** HH:mm (24-uurs, NL-wandklok); alleen zinvol samen met een datum. */
    tijd: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik een tijd als 09:30')
      .optional(),
    tePlannenDoorEmployeeId: z.string().trim().min(1).optional(),
    tePlannenDoorNaam: z.string().trim().max(200).optional(),
    isHuidig: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (!v.isHuidig && !v.datum && !v.tePlannenDoorEmployeeId && !v.tePlannenDoorNaam) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['datum'],
        message: 'Geef een datum of wie dit gesprek plant',
      })
    }
    if (v.tijd && !v.datum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tijd'],
        message: 'Een tijd hoort bij een datum: kies eerst een datum',
      })
    }
  })
export type GespreksverslagVervolgInput = z.infer<typeof gespreksverslagVervolgSchema>

/**
 * Verslag aanmaken/bijwerken (v2-concept). Bewust lenient: de grote vakken
 * mogen leeg blijven zodat tussentijds opslaan nooit blokkeert; de strikte
 * eisen (verslag >= 20 tekens) gelden pas bij definitief maken
 * (`valideerDefinitiefV2`). Actiepunt-regels zijn wél per regel compleet
 * (FO-332 §7).
 */
export const upsertGespreksverslagV2Schema = z.object({
  gespreksDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies de gespreksdatum'),
  onderwerp: z.string().trim().max(300).optional().default(''),
  aanwezig: z.string().trim().max(1000).optional().default(''),
  locatie: z.string().trim().max(300).optional().default(''),
  inleiding: z.string().trim().max(20000).optional().default(''),
  verslag: z.string().trim().max(40000).optional().default(''),
  samenvatting: z.string().trim().max(20000).optional().default(''),
  actiepunten: z.array(gespreksverslagActiepuntSchema).max(50).default([]),
  vervolgAfspraken: z.array(gespreksverslagVervolgSchema).max(20).default([]),
  /** FO-459 (batch-62) - briefpapierkeuze voor het verslag; leeg = vorige keuze/cascade. */
  briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type UpsertGespreksverslagV2Input = z.infer<typeof upsertGespreksverslagV2Schema>

/** FO-459 (batch-62) - POST /gespreksverslagen/:id/preview: optioneel een briefpapierkeuze. */
export const gespreksverslagPreviewSchema = z.object({
  briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type GespreksverslagPreviewInput = z.infer<typeof gespreksverslagPreviewSchema>

/**
 * Definitief-maak-validatie voor v2 (FO-332 §7): het Verslag-veld is verplicht
 * (min. 20 tekens); overige tekstvakken optioneel; nul actiepunten toegestaan.
 * Pure functie zodat API én form dezelfde regels toepassen.
 */
export function valideerDefinitiefV2(input: {
  verslag: string | null | undefined
}): { veld: string; message: string }[] {
  const fouten: { veld: string; message: string }[] = []
  if ((input.verslag ?? '').trim().length < GESPREKSVERSLAG_MIN_TEKENS) {
    fouten.push({
      veld: 'verslag',
      message: `Verslag is verplicht (min. ${GESPREKSVERSLAG_MIN_TEKENS} tekens)`,
    })
  }
  return fouten
}

/** Actiehouder van een actiepunt-regel in de DTO's. */
export interface GespreksverslagHouderDTO {
  id: string
  employeeId: string | null
  /** Naam van de app-medewerker (geresolvet) of null bij een vrije naam. */
  employeeNaam: string | null
  vrijeNaam: string | null
  volgorde: number
}

/** Eén "Volgende afspraak"-regel in de DTO's. */
export interface GespreksverslagVervolgDTO {
  id: string
  gesprekTypeConfigId: string | null
  typeLabel: string
  /** yyyy-MM-dd of null (nog te plannen). */
  datum: string | null
  /** batch-47 (§11a) - HH:mm of null ("in week X" zonder concreet tijdstip). */
  tijd: string | null
  tePlannenDoorEmployeeId: string | null
  /** Naam van de planner (geresolvet of vrije naam). */
  tePlannenDoorNaam: string | null
  isHuidig: boolean
  volgorde: number
  /** HrGesprek-id als de regel al als agenda-item is aangemaakt. */
  agendaGesprekId: string | null
}

/** Autofill-antwoord (FO-312-stijl): voorzet uit agenda-gesprek + medewerker + cyclus. */
export interface GespreksverslagAutofillDTO {
  /** yyyy-MM-dd van het agenda-gesprek (NL-kalenderdag). */
  datum: string
  onderwerp: string
  aanwezig: string
  locatie: string
  /** Cyclus-voorzet uit het gesprekstype (eerste regel = huidig gesprek). */
  cyclus: Array<{
    gesprekTypeConfigId: string | null
    typeLabel: string
    datum: string | null
    tePlannenDoorNaam: string | null
    isHuidig: boolean
  }>
  /** Bron-omschrijving voor de gele velden ("Agenda-gesprek 07-05-2026"). */
  bron: string
}

/**
 * Portal-DTO: wat de medewerker ziet van zijn eigen vrijgegeven verslag. Bevat
 * geen interne velden (opsteller-userId, audits, andere medewerkers).
 */
export interface GespreksverslagPortalDTO {
  id: string
  titel: string
  /** Gesprekstype-label (neutraal "Gesprek" bij verzuim-redactie naar buiten). */
  typeLabel: string
  geplandOp: string
  gesprekspartnerNaam: string | null
  inhoud: GespreksverslagInhoud
  exemplaarNummer: string | null
  /** Document voor PDF-download/ondertekening (scope EMPLOYEE). */
  documentId: string | null
  /** Laatste versie-nummer van het document (de download-link wijst hierheen, niet hardcoded v1). */
  documentVersieNr: number | null
  vrijgegevenOp: string | null
  /** Heeft de medewerker al getekend voor akkoord? */
  getekend: boolean
  getekendOp: string | null
  /** FO-309 P3 — de afspraken (actielijst) die de medewerker aangaan. */
  afspraken: GespreksverslagAfspraakDTO[]
  /** Eigen reacties/bezwaren op dit verslag. */
  reacties: GespreksverslagReactieDTO[]
  // ── batch-39 gesprekken (FO-332) — §2-veldenschema (v2) ──
  veldenVersie: number
  gespreksDatum: string | null
  onderwerp: string | null
  aanwezig: string | null
  locatie: string | null
  inleiding: string | null
  verslag: string | null
  samenvatting: string | null
  /** Stilzwijgend akkoord geregistreerd op (nooit een handtekening). */
  stilzwijgendAkkoordOp: string | null
  vervolgAfspraken: GespreksverslagVervolgDTO[]
  // ── FO-378 (batch-44) — versiebeleid ──
  /**
   * Het versienummer van de getoonde (laatst vrijgegeven) versie. Gelijk aan
   * `documentVersieNr`; 1 zolang er nooit een herziening was.
   */
  versieNr: number
  /** True zodra dit verslag meer dan één vrijgegeven versie heeft gekend. */
  isAangepast: boolean
  /** Eerdere vrijgegeven versies (download-historie), aflopend, zonder de huidige. */
  eerdereVersies: Array<{ versionNr: number; vrijgegevenOp: string }>
  // ── batch-47 gespreksverslagen (spec 90 §3) ──
  /**
   * ISO of null - eigen leesbevestiging op de huidige versie (FO-126).
   * Nadrukkelijk GEEN akkoord: dat is `getekend`.
   */
  leesbevestigingOp: string | null
  /** Is een handtekening van DEZE medewerker verplicht voor deze versie (§6)? */
  moetTekenen: boolean
  /** ISO of null - de medewerker heeft ondertekening geweigerd (§3/T2). */
  geweigerdOp: string | null
  /** Toegestane tekenverklaringen bij dit gesprekstype (§6). */
  tekenverklaringOpties: GespreksverslagTekenverklaring[]
}

/**
 * FO-378 — de bevroren portal-weergave van een vastgelegde versie
 * (GespreksverslagVersie.inhoudSnapshot). Tijdens een open herziening toont de
 * portal deze snapshot i.p.v. de werk-in-uitvoering-kolommen.
 */
export interface GespreksverslagPortalSnapshot {
  inhoud: GespreksverslagInhoud
  veldenVersie: number
  gespreksDatum: string | null
  onderwerp: string | null
  aanwezig: string | null
  locatie: string | null
  inleiding: string | null
  verslag: string | null
  samenvatting: string | null
  afspraken: GespreksverslagAfspraakDTO[]
  vervolgAfspraken: GespreksverslagVervolgDTO[]
}
